import React, { useState, useEffect } from 'react';
import { X, FileText, Users, Wrench, DollarSign, Truck, Clock, ChevronDown, ChevronUp, Download, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

/**
 * MissionBriefViewer - View all 6 ChatGPT-generated Mission Brief documents
 * 
 * Documents:
 * 1. Client Proposal - Professional proposal with timeline, pricing, scope highlights
 * 2. Operator Order - Crew instructions, safety protocols, daily objectives per phase
 * 3. SOW - Client - Detailed scope of work for client approval
 * 4. SOW - Subcontractor - Trade-specific scope packs
 * 5. Milestone Objectives - Payment draws tied to completion milestones
 * 6. Supply Manifest - Phased material list with delivery schedule
 */

const DOCUMENT_TYPES = [
  { key: 'client_proposal', name: 'Client Proposal', icon: FileText, color: '#10b981', description: 'Professional proposal with timeline & pricing' },
  { key: 'operator_order', name: 'Operator Order', icon: Users, color: '#3b82f6', description: 'Crew instructions, safety protocols, daily objectives' },
  { key: 'sow_client', name: 'SOW - Client', icon: Wrench, color: '#8b5cf6', description: 'Detailed scope of work for client approval' },
  { key: 'sow_subcontractor', name: 'SOW - Subcontractor', icon: Wrench, color: '#f59e0b', description: 'Trade-specific scope packs' },
  { key: 'milestone_objectives', name: 'Milestone Objectives', icon: DollarSign, color: '#ec4899', description: 'Payment draws tied to completion milestones' },
  { key: 'supply_manifest', name: 'Supply Manifest', icon: Truck, color: '#06b6d4', description: 'Phased material list with delivery schedule' },
];

export default function MissionBriefViewer({ 
  estimateId, 
  onClose,
  onGenerate // callback to trigger generation if no data exists
}) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [missionBrief, setMissionBrief] = useState(null);
  const [activeDoc, setActiveDoc] = useState('client_proposal');
  const [expandedSections, setExpandedSections] = useState({});

  // Load existing mission brief data
  useEffect(() => {
    const loadMissionBrief = async () => {
      if (!estimateId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await API.get(`/estimates/${estimateId}`);
        const data = response.data?.mission_brief_documents;
        
        if (data) {
          setMissionBrief(data);
          console.log("[MissionBriefViewer] Loaded mission brief:", Object.keys(data));
        }
      } catch (error) {
        console.log("[MissionBriefViewer] No mission brief data found");
      } finally {
        setLoading(false);
      }
    };
    
    loadMissionBrief();
  }, [estimateId]);

  // Generate mission brief using ChatGPT
  const handleGenerate = async () => {
    if (!estimateId) {
      toast.error("No estimate selected");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await API.post(`/estimates/${estimateId}/generate-schedule`, {
        use_chatgpt: true
      });
      
      if (response.data.success) {
        // Reload the estimate to get the new mission brief
        const updatedEstimate = await API.get(`/estimates/${estimateId}`);
        setMissionBrief(updatedEstimate.data?.mission_brief_documents);
        
        toast.success("Mission Brief generated with ChatGPT!", {
          description: `${response.data.total_phases} phases, ${response.data.total_days} days`
        });
      }
    } catch (error) {
      console.error("Generation failed:", error);
      toast.error("Failed to generate Mission Brief");
    } finally {
      setGenerating(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Render document content based on type
  const renderDocumentContent = (docKey, data) => {
    if (!data) return <p className="text-slate-500">No data available</p>;

    switch (docKey) {
      case 'client_proposal':
        return renderClientProposal(data);
      case 'operator_order':
        return renderOperatorOrder(data);
      case 'sow_client':
        return renderSOWClient(data);
      case 'sow_subcontractor':
        return renderSOWSubcontractor(data);
      case 'milestone_objectives':
        return renderMilestoneObjectives(data);
      case 'supply_manifest':
        return renderSupplyManifest(data);
      default:
        return <pre className="text-xs text-slate-400 overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
    }
  };

  // Client Proposal Renderer
  const renderClientProposal = (data) => (
    <div className="space-y-6">
      {/* Executive Summary */}
      {data.executive_summary && (
        <div className="p-4 rounded-lg" style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
          <h3 className="text-emerald-400 font-bold mb-2">EXECUTIVE SUMMARY</h3>
          <p className="text-slate-300 text-sm">{data.executive_summary}</p>
        </div>
      )}
      
      {/* Timeline */}
      {data.timeline && (
        <div className="p-4 rounded-lg bg-slate-800/50">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-emerald-400" />
            PROJECT TIMELINE
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-emerald-400">{data.timeline.working_days?.typical || 'TBD'}</p>
              <p className="text-xs text-slate-500">Working Days</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{data.timeline.calendar_weeks?.typical || 'TBD'}</p>
              <p className="text-xs text-slate-500">Calendar Weeks</p>
            </div>
            <div>
              <p className="text-lg font-bold text-amber-400">{data.timeline.recommended_duration || 'TBD'}</p>
              <p className="text-xs text-slate-500">Recommended</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Scope Highlights */}
      {data.scope_highlights && data.scope_highlights.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-3">SCOPE HIGHLIGHTS</h3>
          <ul className="space-y-2">
            {data.scope_highlights.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                <span className="text-emerald-400 mt-1">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Investment */}
      {data.investment && (
        <div className="p-4 rounded-lg bg-slate-800/50">
          <h3 className="text-white font-bold mb-3">INVESTMENT</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500">Base Investment</p>
              <p className="text-xl font-bold text-white font-mono">${(data.investment.base_contract || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Recommended Contingency</p>
              <p className="text-lg font-bold text-amber-400 font-mono">{data.investment.contingency_recommendation || '10%'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Operator Order Renderer - Week by Week with Weather Buffer
  const renderOperatorOrder = (data) => (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-center">
          <p className="text-2xl font-bold text-blue-400">{data.total_weeks || data.working_weeks || 12}</p>
          <p className="text-xs text-slate-500">Total Weeks</p>
        </div>
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
          <p className="text-2xl font-bold text-emerald-400">{data.working_weeks || 12}</p>
          <p className="text-xs text-slate-500">Working Weeks</p>
        </div>
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
          <p className="text-2xl font-bold text-amber-400">{data.buffer_weeks || 2}</p>
          <p className="text-xs text-slate-500">Buffer Weeks</p>
        </div>
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
          <p className="text-sm font-bold text-red-400">{data.threat_assessment || 'CRITICAL'}</p>
          <p className="text-xs text-slate-500">Status</p>
        </div>
      </div>

      {/* Weekly Schedule */}
      {data.weekly_schedule && data.weekly_schedule.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-400" />
            WEEKLY SCHEDULE ({data.weekly_schedule.length} weeks)
          </h3>
          <div className="space-y-3">
            {data.weekly_schedule.map((week, idx) => {
              const isExpanded = expandedSections[`week-${idx}`];
              return (
                <div key={idx} className="rounded-lg overflow-hidden" style={{ border: "1px solid rgba(59, 130, 246, 0.3)" }}>
                  <button
                    onClick={() => toggleSection(`week-${idx}`)}
                    className="w-full px-4 py-3 flex items-center justify-between"
                    style={{ backgroundColor: "rgba(59, 130, 246, 0.15)" }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-blue-500/20 text-blue-400">
                        W{week.week_number}
                      </span>
                      <div>
                        <span className="text-white font-bold">{week.week_title}</span>
                        <p className="text-xs text-slate-500">{week.focus_area}</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-4 space-y-4 bg-slate-800/30">
                      {week.daily_objectives && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase mb-2">Daily Objectives</p>
                          <ul className="space-y-1">
                            {week.daily_objectives.map((obj, i) => (
                              <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                                <span className="text-blue-400">→</span>
                                {obj}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {week.crew_requirements && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase mb-2">Crew Requirements</p>
                          <div className="flex flex-wrap gap-2">
                            {week.crew_requirements.map((crew, i) => (
                              <span key={i} className="px-2 py-1 rounded text-xs bg-slate-700 text-slate-300">{crew}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {week.safety_protocols && (
                        <div>
                          <p className="text-xs text-red-400 uppercase mb-2">Safety Protocols</p>
                          <ul className="space-y-1">
                            {week.safety_protocols.map((protocol, i) => (
                              <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                                <span className="text-red-400">⚠</span>
                                {protocol}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {week.weather_contingency && (
                        <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                          <p className="text-xs text-amber-400">☁ Weather Note: {week.weather_contingency}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weather Buffer Weeks */}
      {data.weather_buffer_weeks && data.weather_buffer_weeks.length > 0 && (
        <div>
          <h3 className="text-amber-400 font-bold mb-4 flex items-center gap-2">
            ☁ WEATHER/CONTINGENCY BUFFER ({data.weather_buffer_weeks.length} weeks)
          </h3>
          <div className="space-y-3">
            {data.weather_buffer_weeks.map((buffer, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-amber-500/20 text-amber-400">
                    W{buffer.week_number}
                  </span>
                  <span className="text-amber-400 font-bold">{buffer.week_title}</span>
                </div>
                <p className="text-sm text-slate-400 mb-2">{buffer.purpose}</p>
                {buffer.potential_uses && (
                  <ul className="space-y-1">
                    {buffer.potential_uses.map((use, i) => (
                      <li key={i} className="text-xs text-slate-500">• {use}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback for old phases structure */}
      {!data.weekly_schedule && data.phases && data.phases.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4">EXECUTION PHASES</h3>
          <div className="space-y-3">
            {data.phases.map((phase, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-blue-500/20">
                <div className="flex items-center gap-3">
                  <span className="text-blue-400 font-bold">Phase {phase.phase_number || idx + 1}</span>
                  <span className="text-white">{phase.phase_name}</span>
                  <span className="text-emerald-400 text-sm">{phase.week_range}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Critical Path */}
      {data.critical_path && data.critical_path.length > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <h3 className="text-red-400 font-bold mb-2">CRITICAL PATH</h3>
          <ul className="space-y-1">
            {data.critical_path.map((item, idx) => (
              <li key={idx} className="text-sm text-slate-300">{idx + 1}. {item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // SOW Client Renderer
  const renderSOWClient = (data) => (
    <div className="space-y-6">
      {/* Scope Summary */}
      {data.scope_summary && (
        <div className="p-4 rounded-lg bg-slate-800/50">
          <h3 className="text-white font-bold mb-2">SCOPE SUMMARY</h3>
          <p className="text-slate-300 text-sm">{data.scope_summary}</p>
        </div>
      )}
      
      {/* Detailed Sections */}
      {data.detailed_sections && data.detailed_sections.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-3">DETAILED SCOPE</h3>
          {data.detailed_sections.map((section, idx) => (
            <div key={idx} className="mb-4 p-4 rounded-lg bg-slate-800/30">
              <h4 className="text-purple-400 font-bold mb-2">{section.section_name}</h4>
              <ul className="space-y-1">
                {(section.items || []).map((item, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      
      {/* Exclusions */}
      {data.exclusions && data.exclusions.length > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <h3 className="text-red-400 font-bold mb-2">EXCLUSIONS</h3>
          <ul className="space-y-1">
            {data.exclusions.map((item, idx) => (
              <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                <span className="text-red-400">✕</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // SOW Subcontractor Renderer
  const renderSOWSubcontractor = (data) => (
    <div className="space-y-6">
      {/* Trade Packages */}
      {data.trade_packages && data.trade_packages.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4">TRADE PACKAGES</h3>
          <div className="space-y-3">
            {data.trade_packages.map((pkg, idx) => {
              const isExpanded = expandedSections[`trade-${idx}`];
              return (
                <div key={idx} className="rounded-lg overflow-hidden border border-amber-500/30">
                  <button
                    onClick={() => toggleSection(`trade-${idx}`)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-amber-500/10"
                  >
                    <span className="text-amber-400 font-bold">{pkg.trade_name}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-4 bg-slate-800/30">
                      {pkg.scope_items && (
                        <div className="mb-3">
                          <p className="text-xs text-slate-500 mb-2">SCOPE ITEMS</p>
                          <ul className="space-y-1">
                            {pkg.scope_items.map((item, i) => (
                              <li key={i} className="text-sm text-slate-300">• {item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {pkg.specifications && (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">SPECIFICATIONS</p>
                          <ul className="space-y-1">
                            {pkg.specifications.map((spec, i) => (
                              <li key={i} className="text-sm text-slate-400">→ {spec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // Milestone Objectives Renderer - Weekly Milestones without $ amounts
  const renderMilestoneObjectives = (data) => (
    <div className="space-y-6">
      {/* Header Notice */}
      <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-600">
        <p className="text-sm text-slate-400">
          <span className="text-amber-400 font-bold">Note:</span> Dollar amounts are determined by contractor - this document tracks completion milestones only.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-center">
          <p className="text-2xl font-bold text-blue-400">{data.total_weeks || 14}</p>
          <p className="text-xs text-slate-500">Total Weeks</p>
        </div>
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
          <p className="text-2xl font-bold text-emerald-400">{(data.weekly_milestones?.length || 0) + (data.buffer_milestones?.length || 0)}</p>
          <p className="text-xs text-slate-500">Milestones</p>
        </div>
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
          <p className="text-2xl font-bold text-amber-400">{data.buffer_milestones?.length || 2}</p>
          <p className="text-xs text-slate-500">Buffer Weeks</p>
        </div>
      </div>

      {/* Weekly Milestones */}
      {data.weekly_milestones && data.weekly_milestones.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4">WEEKLY MILESTONES</h3>
          <div className="space-y-3">
            {data.weekly_milestones.map((milestone, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-pink-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-pink-500/20 text-pink-400">
                      W{milestone.week}
                    </span>
                    <span className="text-white font-bold">{milestone.milestone_name}</span>
                  </div>
                  <span className="text-xs text-slate-500 px-2 py-1 rounded bg-slate-700">{milestone.phase_reference}</span>
                </div>
                
                {/* Amount placeholder - to be filled by contractor */}
                <div className="flex items-center gap-4 my-2 p-2 rounded bg-slate-900/50 border border-dashed border-slate-600">
                  <div>
                    <p className="text-xs text-slate-500">Percentage</p>
                    <p className="text-slate-400 font-mono">____%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Amount</p>
                    <p className="text-slate-400 font-mono">$________</p>
                  </div>
                </div>
                
                {milestone.completion_criteria && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500">Completion Criteria:</p>
                    <ul className="space-y-1 mt-1">
                      {milestone.completion_criteria.map((criteria, i) => (
                        <li key={i} className="text-sm text-slate-400">✓ {criteria}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buffer Milestones */}
      {data.buffer_milestones && data.buffer_milestones.length > 0 && (
        <div>
          <h3 className="text-amber-400 font-bold mb-4">☁ BUFFER/CONTINGENCY WEEKS</h3>
          <div className="space-y-3">
            {data.buffer_milestones.map((buffer, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-amber-500/20 text-amber-400">
                    W{buffer.week}
                  </span>
                  <span className="text-amber-400 font-bold">{buffer.milestone_name}</span>
                </div>
                {buffer.completion_criteria && (
                  <ul className="space-y-1">
                    {buffer.completion_criteria.map((criteria, i) => (
                      <li key={i} className="text-xs text-slate-400">• {criteria}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback for old draws structure */}
      {!data.weekly_milestones && data.draws && data.draws.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4">PAYMENT MILESTONES ({data.draws.length})</h3>
          <div className="space-y-3">
            {data.draws.map((draw, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-pink-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-bold">{draw.milestone_name || `Draw ${idx + 1}`}</span>
                  <span className="text-slate-500 text-sm">{draw.target_date}</span>
                </div>
                {draw.completion_criteria && (
                  <ul className="space-y-1 mt-1">
                    {draw.completion_criteria.map((criteria, i) => (
                      <li key={i} className="text-sm text-slate-400">✓ {criteria}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Supply Manifest Renderer - Materials ONLY (no labor, no days)
  const renderSupplyManifest = (data) => (
    <div className="space-y-6">
      {/* Header Notice */}
      <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
        <p className="text-sm text-cyan-400 font-bold">
          MATERIAL ORDERING MANIFEST
        </p>
        <p className="text-xs text-slate-400 mt-1">
          This document is for material ordering and vendor coordination only. No labor costs or completion timelines.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 rounded-lg bg-slate-800/50 text-center">
          <p className="text-2xl font-bold text-cyan-400">{data.total_line_items || '-'}</p>
          <p className="text-xs text-slate-500">Total Line Items</p>
        </div>
        <div className="p-3 rounded-lg bg-slate-800/50 text-center">
          <p className="text-2xl font-bold text-white">{data.phases?.length || '-'}</p>
          <p className="text-xs text-slate-500">Delivery Phases</p>
        </div>
      </div>

      {/* Phased Materials */}
      {data.phases && data.phases.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Truck className="h-4 w-4 text-cyan-400" />
            MATERIALS BY PHASE
          </h3>
          <div className="space-y-3">
            {data.phases.map((phase, idx) => {
              const isExpanded = expandedSections[`material-phase-${idx}`];
              return (
                <div key={idx} className="rounded-lg overflow-hidden border border-cyan-500/30">
                  <button
                    onClick={() => toggleSection(`material-phase-${idx}`)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-cyan-500/10"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-cyan-500/20 text-cyan-400">
                        {phase.phase}
                      </span>
                      <div>
                        <span className="text-white font-bold">{phase.phase_name}</span>
                        <p className="text-xs text-slate-500">Delivery: {phase.delivery_target}</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-4 bg-slate-800/30">
                      {phase.materials && phase.materials.length > 0 && (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-slate-500 uppercase border-b border-slate-700">
                              <th className="text-left py-2">Item</th>
                              <th className="text-right py-2">Qty</th>
                              <th className="text-right py-2">Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {phase.materials.map((mat, i) => (
                              <tr key={i} className="border-b border-slate-800">
                                <td className="py-2 text-slate-300">{mat.item}</td>
                                <td className="py-2 text-right text-white font-mono">{mat.qty}</td>
                                <td className="py-2 text-right text-slate-500">{mat.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {phase.vendor_notes && (
                        <p className="mt-3 text-xs text-amber-400 p-2 rounded bg-amber-500/10">
                          📋 {phase.vendor_notes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Long Lead Items */}
      {data.long_lead_items && data.long_lead_items.length > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <h3 className="text-red-400 font-bold mb-3">⚠ LONG LEAD-TIME ITEMS</h3>
          <p className="text-xs text-slate-500 mb-3">Order these items early to avoid project delays</p>
          <div className="space-y-2">
            {data.long_lead_items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                <span className="text-white">{item.item}</span>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-amber-400">Lead: {item.lead_time}</span>
                  <span className="text-red-400 font-bold">Order by: {item.order_by}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback for old delivery_schedule structure */}
      {!data.phases && data.delivery_schedule && data.delivery_schedule.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4">DELIVERY SCHEDULE</h3>
          <div className="space-y-3">
            {data.delivery_schedule.map((delivery, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-cyan-500/20">
                <div className="flex items-center gap-3">
                  <Truck className="h-4 w-4 text-cyan-400" />
                  <span className="text-white font-bold">{delivery.phase}</span>
                  <span className="text-cyan-400 text-sm">{delivery.timing}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Loading Mission Brief...</div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[100] overflow-y-auto pt-16 pb-8 px-6"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Fixed Header */}
      <div 
        className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
        style={{ 
          backgroundColor: "#0f172a",
          borderBottom: "2px solid rgba(16, 185, 129, 0.5)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
        }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all"
        >
          ← Back
        </button>
        
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-emerald-400" />
          <span className="text-emerald-400 font-bold">MISSION BRIEF VIEWER</span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">ChatGPT Generated Documents</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-all disabled:opacity-50"
          >
            {generating ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                {missionBrief ? 'Regenerate' : 'Generate'}
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto mt-4">
        {!missionBrief ? (
          // No data - show generate prompt
          <div className="flex flex-col items-center justify-center py-20">
            <Sparkles className="h-16 w-16 text-slate-600 mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Mission Brief Generated</h2>
            <p className="text-slate-500 mb-6 text-center max-w-md">
              Generate all 6 Mission Brief documents with ChatGPT for accurate timelines, 
              scope of work, payment milestones, and crew instructions.
            </p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-all disabled:opacity-50"
            >
              {generating ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Generating with ChatGPT...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5" />
                  Generate Mission Brief
                </>
              )}
            </button>
          </div>
        ) : (
          // Show documents
          <div className="flex gap-6">
            {/* Document Tabs - Sidebar */}
            <div className="w-64 flex-shrink-0">
              <div className="sticky top-20 space-y-2">
                {DOCUMENT_TYPES.map((doc) => {
                  const Icon = doc.icon;
                  const isActive = activeDoc === doc.key;
                  const hasData = missionBrief[doc.key];
                  
                  return (
                    <button
                      key={doc.key}
                      onClick={() => setActiveDoc(doc.key)}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        isActive ? 'ring-2' : 'hover:bg-slate-800/50'
                      }`}
                      style={{
                        backgroundColor: isActive ? `${doc.color}20` : 'transparent',
                        borderColor: isActive ? doc.color : 'transparent',
                        ringColor: isActive ? doc.color : 'transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" style={{ color: doc.color }} />
                        <div>
                          <p className={`font-bold text-sm ${isActive ? 'text-white' : 'text-slate-400'}`}>
                            {doc.name}
                          </p>
                          {!hasData && (
                            <p className="text-xs text-red-400">Not generated</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Document Content */}
            <div className="flex-1 min-w-0">
              <div 
                className="rounded-lg p-6"
                style={{ 
                  backgroundColor: "#0f172a",
                  border: `1px solid ${DOCUMENT_TYPES.find(d => d.key === activeDoc)?.color}40`
                }}
              >
                {/* Document Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-700">
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {DOCUMENT_TYPES.find(d => d.key === activeDoc)?.name}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {DOCUMENT_TYPES.find(d => d.key === activeDoc)?.description}
                    </p>
                  </div>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-800 text-slate-400 hover:text-white text-sm">
                    <Download className="h-4 w-4" />
                    Export
                  </button>
                </div>
                
                {/* Document Body */}
                {renderDocumentContent(activeDoc, missionBrief[activeDoc])}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
